import React from 'react';
import { motion } from 'framer-motion';
import { Compass, Moon, Sun, ArrowRight, Shield, Globe, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LandingPage = ({ isDarkMode, setIsDarkMode }) => {
    const navigate = useNavigate();

    return (
        <div className={`min-h-screen transition-colors duration-500 font-sans overflow-hidden relative ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-[#FCFDFE] text-slate-800'
            }`}>
            {/* Background Orbs */}
            {isDarkMode ? (
                <>
                    <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />
                    <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
                </>
            ) : (
                <>
                    <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-400/10 rounded-full blur-[120px] pointer-events-none" />
                    <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-[120px] pointer-events-none" />
                </>
            )}

            {/* Navigation */}
            <nav className="relative z-20 flex justify-between items-center p-6 max-w-7xl mx-auto">
                <div className="flex items-center space-x-3">
                    <img
                        src="/icon-192x192.png"
                        alt="Swara Shastra Logo"
                        className={`w-12 h-12 object-contain rounded-xl ${isDarkMode ? 'shadow-2xl shadow-blue-500/20' : 'shadow-sm'}`}
                    />
                    <span className={`text-xl font-black tracking-tighter ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        SWARA SHASTRA
                    </span>
                </div>
                <div className="flex items-center space-x-6">
                    {/* Theme Toggle Button */}
                    <button
                        onClick={() => setIsDarkMode(!isDarkMode)}
                        className={`p-2 rounded-xl transition-all ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-yellow-400' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                            }`}
                        title={isDarkMode ? "Switch to Celestial Light" : "Switch to Premium Dark"}
                    >
                        {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    </button>

                    <button
                        onClick={() => navigate('/login')}
                        className={`font-bold text-sm transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'
                            }`}
                    >
                        Sign In
                    </button>
                    <button
                        onClick={() => navigate('/signup')}
                        className={`px-6 py-3 rounded-2xl font-black text-sm transition-all active:scale-95 shadow-lg ${isDarkMode
                                ? 'bg-white text-black hover:bg-gray-200 shadow-white/5'
                                : 'bg-slate-900 text-white hover:bg-slate-800 shadow-slate-900/10'
                            }`}
                    >
                        Join Free
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            <main className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-40">
                <div className="max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className={`inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border mb-8 shadow-sm ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-blue-100'
                            }`}>
                            <span className="text-blue-500 font-black text-[10px] uppercase tracking-widest">Ida</span>
                            <div className={`w-1 h-1 rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-slate-300'}`} />
                            <span className="text-orange-500 font-black text-[10px] uppercase tracking-widest">Pingala</span>
                        </div>

                        <h1 className={`text-6xl sm:text-8xl font-black tracking-tight leading-[0.95] mb-8 uppercase ${isDarkMode ? 'text-white' : 'text-slate-900'
                            }`}>
                            {isDarkMode ? 'Master the' : 'The Rhythm of'} <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-orange-500">
                                {isDarkMode ? 'Ancient Breath' : 'Celestial Breath'}
                            </span>.
                        </h1>

                        <p className={`text-lg sm:text-xl font-medium mb-12 leading-relaxed max-w-2xl ${isDarkMode ? 'text-gray-400' : 'text-slate-600'
                            }`}>
                            Unlock the secret duality of Svara Shastra. Journey between the cooling Lunar path of <span className="text-blue-500 font-bold">Ida</span> and the heating Solar path of <span className="text-orange-500 font-bold">Pingala</span> to master your life's harmony.
                        </p>

                        <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-5">
                            <button
                                onClick={() => navigate('/signup')}
                                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-orange-500 px-10 py-5 rounded-[24px] font-black text-white text-lg hover:shadow-2xl hover:shadow-orange-500/20 transition-all active:scale-[0.98] flex items-center justify-center space-x-3 group"
                            >
                                <span>Begin Your Practice</span>
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <button
                                onClick={() => navigate('/pricing')}
                                className={`w-full sm:w-auto border px-10 py-5 rounded-[24px] font-black text-lg transition-all active:scale-[0.98] ${isDarkMode
                                        ? 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                                        : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'
                                    }`}
                            >
                                View Plans
                            </button>
                        </div>
                    </motion.div>
                </div>

                {/* Decorative Elements */}
                <div className={`absolute right-[5%] top-[15%] hidden lg:block pointer-events-none ${isDarkMode ? 'opacity-20' : 'opacity-100'}`}>
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 120, repeat: Infinity, ease: "linear" }}
                        className="relative w-[500px] h-[500px]"
                    >
                        <div className={`absolute top-0 right-0 w-[500px] h-[500px] border-[20px] rounded-full ${isDarkMode ? 'border-white/5' : 'border-orange-500/5'}`} />
                        <div className={`absolute top-0 left-0 w-[500px] h-[500px] border-[1px] rounded-full border-dashed ${isDarkMode ? 'border-blue-500/20' : 'border-blue-500/20'}`} />
                    </motion.div>

                    {!isDarkMode && (
                        <>
                            <motion.div
                                animate={{ y: [0, -20, 0] }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute top-20 right-40 p-6 bg-white rounded-3xl shadow-xl border border-blue-50"
                            >
                                <Moon className="w-12 h-12 text-blue-500" />
                            </motion.div>
                            <motion.div
                                animate={{ y: [0, 20, 0] }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                                className="absolute bottom-20 left-10 p-6 bg-white rounded-3xl shadow-xl border border-orange-50"
                            >
                                <Sun className="w-12 h-12 text-orange-500" />
                            </motion.div>
                        </>
                    )}
                </div>
            </main>

            {/* Duality Section */}
            <section className={`relative z-10 max-w-7xl mx-auto px-6 py-40 border-t ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className={`p-10 rounded-[40px] border transition-colors ${isDarkMode ? 'bg-white/5 border-white/5' : 'bg-blue-50/30 border-blue-100/50'
                        }`}>
                        <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-blue-500/20">
                            <Moon className="text-white w-8 h-8" />
                        </div>
                        <h3 className={`text-3xl font-black mb-6 uppercase ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Ida ( Chandra )
                        </h3>
                        <p className={`text-lg font-medium leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-slate-600'}`}>
                            The left nostril path. Representing feminine energy, comfort, and cooling. Perfect for mental work, healing, and creativity.
                        </p>
                    </div>
                    <div className={`p-10 rounded-[40px] border transition-colors ${isDarkMode ? 'bg-white/5 border-white/5' : 'bg-orange-50/30 border-orange-100/50'
                        }`}>
                        <div className="w-14 h-14 bg-orange-500 rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-orange-500/20">
                            <Sun className="text-white w-8 h-8" />
                        </div>
                        <h3 className={`text-3xl font-black mb-6 uppercase ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Pingala ( Surya )
                        </h3>
                        <p className={`text-lg font-medium leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-slate-600'}`}>
                            The right nostril path. Representing masculine energy, vitality, and heat. Best for physical exertion, digestion, and logical analysis.
                        </p>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className={`relative z-10 border-t py-16 px-6 transition-colors ${isDarkMode ? 'border-white/5 bg-[#080808]' : 'border-slate-100 bg-white'
                }`}>
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center text-xs font-bold uppercase tracking-widest text-gray-500">
                    <div className="flex flex-col items-center md:items-start mb-8 md:mb-0">
                        <span className={`text-lg mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Swara Shastra</span>
                        <p>© 2026 Ancient Wisdom for the Modern Breath.</p>
                    </div>
                    <div className="flex space-x-10">
                        <a href="#" className="hover:text-blue-500 transition-colors">Ida Path</a>
                        <a href="#" className="hover:text-orange-500 transition-colors">Pingala Path</a>
                        <a href="#" className="hover:text-white transition-colors">Contact</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
